import { Check } from '@griply/assets';
import { Reminder, ReminderBasedOn } from '@griply/types';
import {
  addReminder,
  getDefaultReminderBasedOn,
  getNonPresetReminders,
  getReminderLabel,
  getReminderOptions,
  isSameReminder,
  parseRelativeReminderOffset,
  sortReminders,
  toggleReminder,
} from '@griply/utils';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { SliderButtons } from 'shared/components/ui/slider-buttons';
import { InteractionType } from 'shared/types/data-type';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { parseTimeToMsOfDay } from 'shared/utils/parse-time-to-ms-of-day';
import { processStringToHoursAndMinutes } from 'shared/utils/process-string-to-hours-and-minutes';
import { v4 as uuidv4 } from 'uuid';

import { ReminderOffsetInputRow } from './reminder-offset-input-row';
import { ReminderTimeInputRow } from './reminder-time-input-row';
import * as Styled from './reminders-popup-menu.style';

export type RemindersPopupMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  reminders?: Reminder[] | null;
  hasTime: boolean;
  timeFormat: TimeFormatOptions;
  onChange: (reminders: Reminder[]) => void;
  onClose: () => void;
};

export const RemindersPopupMenu: React.FC<RemindersPopupMenuProps> = ({
  reminders,
  hasTime,
  timeFormat,
  onChange,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation();

  const [basedOn, setBasedOn] = useState(() =>
    getDefaultReminderBasedOn(reminders, hasTime),
  );
  const [hoursValue, setHoursValue] = useState('');
  const [minutesValue, setMinutesValue] = useState('');
  const [timeValue, setTimeValue] = useState('');
  const [inputError, setInputError] = useState(false);

  const activeReminders = reminders ?? [];
  const isFixed = basedOn === ReminderBasedOn.DayStart;
  const options = getReminderOptions(basedOn);
  const customReminders = sortReminders(
    getNonPresetReminders(activeReminders, options).filter(
      (reminder) => reminder.basedOn === basedOn,
    ),
  );

  const modeOptions = useMemo(
    () =>
      [
        [
          {
            label: t('reminders.modes.relative'),
            value: ReminderBasedOn.Start,
          },
          {
            label: t('reminders.modes.fixed'),
            value: ReminderBasedOn.DayStart,
          },
        ],
      ] as React.ComponentProps<
        typeof SliderButtons<ReminderBasedOn>
      >['optionGroups'],
    [t],
  );

  const onMode = (mode: ReminderBasedOn) => {
    setBasedOn(mode);
    setHoursValue('');
    setMinutesValue('');
    setTimeValue('');
    setInputError(false);
  };

  const onToggle = (offset: number) =>
    onChange(
      toggleReminder(activeReminders, { id: uuidv4(), offset, basedOn }),
    );

  const onChangeHours = (value: string) => {
    setInputError(false);
    setHoursValue(value);
  };

  const onChangeMinutes = (value: string) => {
    setInputError(false);
    setMinutesValue(value);
  };

  const onChangeTime = (value: string) => {
    setInputError(false);
    setTimeValue(
      value.length ? processStringToHoursAndMinutes(value, timeFormat) : value,
    );
  };

  const commitOffset = (offset: number | null) => {
    if (offset === null) {
      setInputError(true);
      return false;
    }

    onChange(addReminder(activeReminders, { id: uuidv4(), offset, basedOn }));
    return true;
  };

  const onSubmitOffset = () => {
    if (!commitOffset(parseRelativeReminderOffset(hoursValue, minutesValue))) {
      return;
    }

    setHoursValue('');
    setMinutesValue('');
  };

  const onSubmitTime = () => {
    if (!commitOffset(parseTimeToMsOfDay(timeValue, timeFormat))) {
      return;
    }

    setTimeValue('');
  };

  return (
    <PopupMenu
      interactionType={InteractionType.Task}
      {...rest}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        <Styled.ModeContainer>
          <SliderButtons
            optionGroups={modeOptions}
            value={basedOn}
            onOption={onMode}
            hasDarkBackground
            isFullWidth
          />
        </Styled.ModeContainer>
      </PopupMenuList>

      <PopupMenuList hasBottomBorder isScrollable>
        {options.map((option) => {
          const selected = activeReminders.some((reminder) =>
            isSameReminder(reminder, option),
          );
          const onItem = () => onToggle(option.offset);

          return (
            <PopupMenuListItem key={option.labelKey}>
              <PopupMenuButton
                end={
                  selected ? <Icon icon={Check} strokeWidth={2} /> : undefined
                }
                isSelected={selected}
                onClick={onItem}
                tabIndex={0}
                type="button"
              >
                {t(option.labelKey)}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}

        {customReminders.map((reminder) => {
          const onItem = () => onToggle(reminder.offset);
          const label =
            getReminderLabel(reminder, t, timeFormat) ??
            parseDateTime(reminder.offset, timeFormat);

          return (
            <PopupMenuListItem
              key={reminder.id ?? `${reminder.basedOn}-${reminder.offset}`}
            >
              <PopupMenuButton
                end={<Icon icon={Check} strokeWidth={2} />}
                isSelected
                onClick={onItem}
                tabIndex={0}
                type="button"
              >
                {label}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>

      <PopupMenuList>
        <PopupMenuListItem>
          {isFixed ? (
            <ReminderTimeInputRow
              label={t('reminders.fixed-time-label')}
              placeholder={t(
                `forms.habit.fields.reminder-time.${timeFormat === TimeFormatOptions.Military ? 'military' : 'standard'}.placeholder`,
              )}
              value={timeValue}
              hasError={inputError}
              onChange={onChangeTime}
              onSubmit={onSubmitTime}
            />
          ) : (
            <ReminderOffsetInputRow
              hours={hoursValue}
              minutes={minutesValue}
              hasError={inputError}
              onChangeHours={onChangeHours}
              onChangeMinutes={onChangeMinutes}
              onSubmit={onSubmitOffset}
            />
          )}
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
